/**
 * What a line drawn on the canvas means for the draft: an AI input or a step's output field into an
 * argument, an argument onto the AI card (a new input), or one step's header onto another's (run
 * after it). A line that would make a step wait for itself, directly or through others, is refused.
 */

import type { ToolField } from '@/modules/back-testing/datasets/tools/tool-schema';
import type { CustomToolStep } from '@/types/custom-tool.types';
import { canWaitFor, withAfter, withAiInput, withArg, type CustomToolDraft } from '../custom-tool-draft';
import type { WireData } from './flow-layout';
import { argOfPort, INPUTS_NODE, inputOfPort, NEW_INPUT_PORT, pathOfPort, RUN_FROM_PORT, RUN_TO_PORT, stepIdOfNode } from './ports';

type Wire =
	| { kind: 'input'; stepId: string; field: string; input: string | null }
	| { kind: 'step'; stepId: string; field: string; from: string; path: string }
	| { kind: 'after'; stepId: string; dep: string };

/** The two ends of a line being drawn or already drawn. */
interface LineEnds {
	source: string;
	target: string;
	sourceHandle?: string | null;
	targetHandle?: string | null;
}

/** The wire a line draws, or null when its two ends do not make one. */
export const wireOf = (connection: LineEnds): Wire | null => {
	const stepId = stepIdOfNode(connection.target);
	if (!stepId) return null;
	if (connection.sourceHandle === RUN_FROM_PORT && connection.targetHandle === RUN_TO_PORT) {
		const dep = stepIdOfNode(connection.source);
		return dep ? { kind: 'after', stepId, dep } : null;
	}
	const field = argOfPort(connection.targetHandle);
	if (!field) return null;
	if (connection.source === INPUTS_NODE) {
		if (connection.sourceHandle === NEW_INPUT_PORT) return { kind: 'input', stepId, field, input: null };
		const input = inputOfPort(connection.sourceHandle);
		return input ? { kind: 'input', stepId, field, input } : null;
	}
	const from = stepIdOfNode(connection.source);
	const path = pathOfPort(connection.sourceHandle);
	return from && path ? { kind: 'step', stepId, field, from, path } : null;
};

export const canDraw = (draft: CustomToolDraft, wire: Wire): boolean => {
	if (wire.kind === 'after') return canWaitFor(draft, wire.stepId, wire.dep);
	if (wire.kind === 'step') return canWaitFor(draft, wire.stepId, wire.from);
	return true;
};

/** The draft with the wire drawn; a wire into an argument that is already set replaces it. */
export const withWire = (
	draft: CustomToolDraft,
	wire: Wire,
	fieldOf: (step: CustomToolStep, name: string) => ToolField | undefined
): CustomToolDraft => {
	const step = draft.steps.find((candidate) => candidate.id === wire.stepId);
	if (!step || !canDraw(draft, wire)) return draft;
	if (wire.kind === 'after') return withAfter(draft, step.id, [...step.after, wire.dep]);
	if (wire.kind === 'step') return withArg(draft, step.id, wire.field, { source: 'step', step: wire.from, path: wire.path });
	if (wire.input) return withArg(draft, step.id, wire.field, { source: 'input', input: wire.input });
	const field = fieldOf(step, wire.field);
	return field ? withAiInput(draft, step.id, field) : draft;
};

export const withoutWire = (draft: CustomToolDraft, wire: WireData): CustomToolDraft => {
	if (wire.kind !== 'after') return withArg(draft, wire.stepId, wire.field, null);
	const step = draft.steps.find((candidate) => candidate.id === wire.stepId);
	return step ? withAfter(draft, step.id, step.after.filter((id) => id !== wire.dep)) : draft;
};
